import React, { useEffect, useRef, useState } from "react"
import { Card, Button, Avatar, Space, Flex, Tooltip } from "antd"
import TextArea from "antd/es/input/TextArea"
import {
  CloseOutlined,
  SendOutlined,
  ExpandOutlined,
  ShrinkOutlined,
  DeleteOutlined,
  HistoryOutlined,
  EditOutlined,
  ArrowLeftOutlined,
  LoadingOutlined,
  PaperClipOutlined
} from "@ant-design/icons"
import classNames from "classnames"
import PropTypes from "prop-types"
import { useSelector, useDispatch } from "react-redux"
import { closeChatCard } from "../../../store/aiassistant"
import ai from "assets/icons/ai.svg"
import styles from "./ChatBox.module.css"
import { Text } from "ui/Typography"
import SystemPrompt from "./SystemPrompt"
import { renderAIMessage } from "./utils"
import { useTranslation } from "react-i18next"
import { useChatSession } from "./useChatSession"
import AddCardSection from "./AddCardSection"
import { spacings } from "themes/variables"

const ThinkingDots = () => (
  <span className={styles.thinkingDots}>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
    <span className={styles.dot}></span>
  </span>
)

function groupSessionsByDate(sessions) {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const yesterdayStart = todayStart - 86400000
  const weekStart = todayStart - 6 * 86400000

  return sessions.reduce(
    (groups, s) => {
      const ts = s.timestamp || 0
      if (ts >= todayStart) groups.Today.push(s)
      else if (ts >= yesterdayStart) groups.Yesterday.push(s)
      else if (ts >= weekStart) groups["This week"].push(s)
      else groups.Older.push(s)
      return groups
    },
    { Today: [], Yesterday: [], "This week": [], Older: [] }
  )
}

const HistoryPanel = ({ sessions, isLoadingHistory, deletingSessionId, onLoad, onDelete, onClose }) => {
  const groups = groupSessionsByDate(sessions)

  return (
    <div className={styles.historyPanel}>
      <Flex align="center" gap={spacings.S} className={styles.historyHeader}>
        <Button type="text" icon={<ArrowLeftOutlined />} aria-label="Back" onClick={onClose} />
        <Text fw="600" className={styles.historyTitle}>
          Chat History
        </Text>
      </Flex>

      <div className={styles.historyList}>
        {isLoadingHistory && (
          <div className={styles.historyEmpty}>
            <LoadingOutlined />
          </div>
        )}
        {!isLoadingHistory && sessions.length === 0 && <div className={styles.historyEmpty}>No previous conversations yet.</div>}
        {!isLoadingHistory &&
          Object.entries(groups).map(([label, items]) =>
            items.length === 0 ? null : (
              <div key={label}>
                <div className={styles.historyGroupLabel}>{label}</div>
                {items.map((s) => (
                  <div key={s.sessionId} className={styles.historyItem}>
                    <button className={styles.historyItemBtn} onClick={() => onLoad(s)}>
                      <span className={styles.historyItemTitle}>{s.title}</span>
                    </button>
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        loading={deletingSessionId === s.sessionId}
                        disabled={deletingSessionId !== null}
                        aria-label="Delete session"
                        className={styles.historyDeleteBtn}
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(s.sessionId)
                        }}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            )
          )}
      </div>
    </div>
  )
}

HistoryPanel.propTypes = {
  sessions: PropTypes.array.isRequired,
  isLoadingHistory: PropTypes.bool.isRequired,
  deletingSessionId: PropTypes.string,
  onLoad: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

const RECEIPT_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,application/pdf"

const ChatBox = () => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false)
  const [selectedPrompt, setSelectedPrompt] = useState(null)
  const [isHistoryVisible, setIsHistoryVisible] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [message, setMessage] = useState("")
  const [attachedFile, setAttachedFile] = useState(null)
  const [attachedPreview, setAttachedPreview] = useState(null)
  const fileInputRef = useRef(null)
  const scrollAnchorRef = useRef(null)
  const dispatch = useDispatch()
  const isOpen = useSelector(({ chatCard }) => chatCard.isOpen)
  const { t } = useTranslation()

  const {
    sessionId,
    chatHistory,
    sessions,
    isLoading,
    isLoadingHistory,
    deletingSessionId,
    startNewSession,
    sendMessage,
    submitCard,
    loadSessions,
    loadSession,
    deleteSession,
    markOptionSelected
  } = useChatSession()

  const lastAiMsg = [...chatHistory].reverse().find((m) => m.role === "ai")
  const fileUploadRequired = !!lastAiMsg?.fileUpload
  const addCard = lastAiMsg?.addCard || null

  useEffect(() => {
    if (isOpen && !sessionId) startNewSession()
  }, [isOpen, sessionId])

  useEffect(() => {
    if (chatHistory.length > 0 || isLoading || isMainMenuOpen) {
      scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth" })
    }
  }, [chatHistory, isLoading, isMainMenuOpen])

  const clearAttachment = () => {
    if (attachedPreview) URL.revokeObjectURL(attachedPreview)
    setAttachedFile(null)
    setAttachedPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (attachedPreview) URL.revokeObjectURL(attachedPreview)
    setAttachedFile(file)
    setAttachedPreview(file.type === "application/pdf" ? null : URL.createObjectURL(file))
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleSendMessage = async (text) => {
    const query = (text ?? message).trim()
    if (!query && !attachedFile) return
    const fileToSend = attachedFile
    setMessage("")
    setIsMainMenuOpen(false)
    setSelectedPrompt(null)
    clearAttachment()
    await sendMessage(query, fileToSend)
  }

  const handleNewChat = () => {
    startNewSession()
    setMessage("")
    clearAttachment()
    setIsMainMenuOpen(false)
    setSelectedPrompt(null)
    setIsHistoryVisible(false)
  }

  const handleShowHistory = () => {
    loadSessions()
    setIsHistoryVisible(true)
  }

  const handleClose = () => {
    dispatch(closeChatCard())
    setMessage("")
    clearAttachment()
  }

  return (
    isOpen && (
      <Card
        title={
          <Flex className={styles.headerSpace}>
            <Flex justify="center" align="center" gap={spacings.S} className={styles.headerTitle}>
              <Avatar src={ai} size={32} className={styles.headerAvatar} />
              <Text fw="500">{t("ai_assistant.ds_ai_assistant")}</Text>
            </Flex>
            <Space>
              <Tooltip title="New chat">
                <Button type="text" icon={<EditOutlined />} aria-label="New chat" className={styles.headerAction} onClick={handleNewChat} />
              </Tooltip>
              <Tooltip title="Chat history">
                <Button
                  type="text"
                  icon={<HistoryOutlined />}
                  aria-label="Chat history"
                  aria-pressed={isHistoryVisible}
                  className={classNames(styles.headerAction, isHistoryVisible && styles.headerActionActive)}
                  onClick={handleShowHistory}
                />
              </Tooltip>
              <span className={styles.expandBtn}>
                {isFullscreen ? (
                  <Button
                    type="text"
                    icon={<ShrinkOutlined />}
                    className={styles.headerAction}
                    aria-label="Shrink"
                    onClick={() => setIsFullscreen(false)}
                  />
                ) : (
                  <Button
                    type="text"
                    icon={<ExpandOutlined />}
                    className={styles.headerAction}
                    aria-label="Expand"
                    onClick={() => setIsFullscreen(true)}
                  />
                )}
              </span>
              <Button type="text" icon={<CloseOutlined />} aria-label="Close" className={styles.headerAction} onClick={handleClose} />
            </Space>
          </Flex>
        }
        className={classNames(styles.chatCard, isFullscreen && styles.fullscreen)}
        styles={{ body: { padding: 0 } }}
      >
        {isHistoryVisible && (
          <HistoryPanel
            sessions={sessions}
            isLoadingHistory={isLoadingHistory}
            deletingSessionId={deletingSessionId}
            onLoad={(session) => {
              loadSession(session)
              setIsHistoryVisible(false)
            }}
            onDelete={(sid) => deleteSession(sid, sessionId, handleNewChat)}
            onClose={() => setIsHistoryVisible(false)}
          />
        )}

        <div className={styles.chatBody}>
          <div style={{ flex: 1 }} />
          <Flex vertical gap={spacings.M}>
            <Flex vertical gap={spacings.L} role="log" aria-live="polite" aria-label="Chat messages">
              {chatHistory.length === 0 && (
                <SystemPrompt
                  selectedPrompt={selectedPrompt}
                  handlePromptClick={(title) => {
                    setMessage(title)
                    setSelectedPrompt(title)
                  }}
                />
              )}
              {chatHistory.map((msg, idx) => {
                const lastAiIdx = chatHistory.reduce((last, m, i) => m.role === "ai" ? i : last, -1)
                return msg.role === "user" ? (
                  <Flex key={msg.id} justify="end" className={classNames("mr-s", styles.userMessage)}>
                    <Flex vertical align="end" gap={spacings.XXS}>
                      {msg.filePreview && (
                        <img src={msg.filePreview} alt="" className={styles.messageImage} />
                      )}
                      {msg.content && <Text className={styles.aiMessage}>{msg.content}</Text>}
                    </Flex>
                  </Flex>
                ) : (
                  <Flex gap={spacings.S} key={msg.id}>
                    <Avatar src={ai} size={32} className={styles.headerAvatar} />
                    <div className={styles.aiMessage}>
                      {renderAIMessage(msg, (opt) => {
                        markOptionSelected(msg.id, opt)
                        handleSendMessage(opt)
                      }, idx === lastAiIdx)}
                    </div>
                  </Flex>
                )
              })}

              {isMainMenuOpen && (
                <SystemPrompt
                  selectedPrompt={selectedPrompt}
                  handlePromptClick={(title) => {
                    setMessage(title)
                    setSelectedPrompt(title)
                  }}
                />
              )}

              {isLoading && (
                <Flex gap={spacings.M} align="center">
                  <Avatar src={ai} size={32} className={styles.headerAvatar} />
                  <ThinkingDots />
                </Flex>
              )}
              <div ref={scrollAnchorRef} />
            </Flex>
          </Flex>
        </div>

        {chatHistory.length > 0 && (
          <Button className={styles.mainMenuButton} disabled={isLoading} onClick={() => setIsMainMenuOpen(true)}>
            {t("ai_assistant.main_menu")}
          </Button>
        )}

        <div className={styles.footer}>
          {addCard && (
            <div className={styles.fileUploadArea}>
              <AddCardSection disabled={isLoading} onSubmitted={(paymentMethodId) => submitCard(addCard, paymentMethodId)} />
            </div>
          )}
          {fileUploadRequired && (
            <div className={styles.fileUploadArea}>
              <input
                ref={fileInputRef}
                type="file"
                accept={RECEIPT_ACCEPT}
                className={styles.hiddenFileInput}
                onChange={handleFileChange}
              />
              {attachedFile ? (
                <div className={styles.attachmentPreview}>
                  {attachedPreview
                    ? <img src={attachedPreview} alt="attachment" className={styles.attachmentThumb} />
                    : <div className={styles.attachmentThumb}><PaperClipOutlined /> {attachedFile.name}</div>
                  }
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    className={styles.attachmentRemove}
                    aria-label="Remove attachment"
                    onClick={clearAttachment}
                  />
                </div>
              ) : (
                <button className={styles.fileUploadTrigger} disabled={isLoading} onClick={() => fileInputRef.current?.click()}>
                  <PaperClipOutlined />
                  <span>Upload receipt (image or PDF)</span>
                </button>
              )}
            </div>
          )}
          <Flex align="center">
            <TextArea
              placeholder={t("ai_assistant.type_a_message")}
              aria-label={t("ai_assistant.type_a_message")}
              className={styles.inputBox}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
              autoSize={{ minRows: 1, maxRows: 4 }}
            />
            <Button
              disabled={isLoading || (!message.trim() && !attachedFile)}
              type="primary"
              icon={<SendOutlined />}
              aria-label="Send message"
              className={styles.sendButton}
              onClick={() => handleSendMessage()}
            />
          </Flex>
        </div>
      </Card>
    )
  )
}

export default ChatBox
